export const GEMINI_LIVE_API_ENDPOINT =
  "wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContent";

export const GEMINI_LIVE_MODEL_ID = "gemini-3.1-flash-live-preview";
export const GEMINI_LIVE_BIDI_MODEL = `models/${GEMINI_LIVE_MODEL_ID}`;
export const GEMINI_LIVE_USAGE_MODEL = GEMINI_LIVE_MODEL_ID;
export const DEFAULT_NARRATION_VOICE = "";
export const USER_GEMINI_API_KEY_STORAGE_KEY = "curio.userGeminiApiKey.v1";

export type NarrationMode =
  | "overview"
  | "anecdote"
  | "historyOverview"
  | "historicPeriod"
  | "understandCard"
  | (string & {});

export interface NarrationPromptInput {
  topicTitle: string;
  narrationMode?: NarrationMode;
  languageCode?: string;
  topicCountry?: string;
  topicSecondMostZoomedOutFeature?: string;
  period?: string;
  priorAnecdotes?: string[];
  knownTranscriptSnippets?: string[];
  cardTitle?: string;
  cardBody?: string;
}

export interface LiveSetupInput {
  languageCode?: string;
  voiceName?: string;
  enableGoogleSearch?: boolean;
  systemInstructionText?: string;
}

export interface NormalizedNarrationRequest extends Required<NarrationPromptInput> {
  voiceName: string;
  enableGoogleSearch: boolean;
}

export interface GeminiLiveSetupPayload {
  setup: {
    model: string;
    systemInstruction: {
      parts: Array<{ text: string }>;
    };
    generationConfig: {
      responseModalities: ["AUDIO"];
      thinkingConfig: {
        thinkingLevel: "minimal";
      };
      speechConfig?: {
        voiceConfig: {
          prebuiltVoiceConfig: {
            voiceName: string;
          };
        };
      };
    };
    outputAudioTranscription: Record<string, never>;
    tools?: Array<{ googleSearch: Record<string, never> }>;
  };
}

function cleanString(value: unknown): string {
  return String(value || "").trim();
}

function cleanStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map(cleanString).filter(Boolean)
    : [];
}

export function extractGeminiApiKey(rawValue: unknown): string {
  const input = cleanString(rawValue);
  if (!input) return "";

  const keyMatch = input.match(/\bAIza[0-9A-Za-z_-]{20,}\b/);
  if (keyMatch) return keyMatch[0];

  return input.replace(/^['"]+|['"]+$/g, "").trim();
}

export function looksLikeGeminiApiKey(rawValue: unknown): boolean {
  return /^AIza[0-9A-Za-z_-]{20,}$/.test(extractGeminiApiKey(rawValue));
}

export function parseJsonStringArray(rawValue: unknown): string[] {
  if (!rawValue) return [];
  try {
    return cleanStringArray(JSON.parse(String(rawValue)));
  } catch {
    return [];
  }
}

export function normalizeSpeechLanguageCode(rawLanguageCode: unknown): string {
  const input = cleanString(rawLanguageCode);
  if (!input) return "en-US";

  const lower = input.toLowerCase();
  if (lower === "en") return "en-US";
  if (lower === "de") return "de-DE";
  if (lower === "fr") return "fr-FR";
  if (lower === "es") return "es-ES";
  if (lower === "it") return "it-IT";
  if (lower === "pt") return "pt-BR";
  if (lower === "nl") return "nl-NL";

  return input;
}

export function describeLanguageForPrompt(rawLanguageCode: unknown): string {
  const lower = cleanString(rawLanguageCode).toLowerCase();
  if (!lower) return "";
  if (lower.startsWith("en")) return "English";
  if (lower.startsWith("de")) return "German";
  if (lower.startsWith("fr")) return "French";
  if (lower.startsWith("es")) return "Spanish";
  if (lower.startsWith("it")) return "Italian";
  if (lower.startsWith("pt")) return "Portuguese";
  if (lower.startsWith("nl")) return "Dutch";
  if (lower.startsWith("ja")) return "Japanese";
  if (lower.startsWith("ko")) return "Korean";
  if (lower.startsWith("zh") || lower.startsWith("cmn")) return "Chinese";
  return cleanString(rawLanguageCode);
}

export function normalizeNarrationRequest(input: NarrationPromptInput & Partial<LiveSetupInput>): NormalizedNarrationRequest {
  return {
    topicTitle: cleanString(input.topicTitle),
    narrationMode: cleanString(input.narrationMode) || "overview",
    languageCode: normalizeSpeechLanguageCode(input.languageCode || "en-US"),
    topicCountry: cleanString(input.topicCountry),
    topicSecondMostZoomedOutFeature: cleanString(input.topicSecondMostZoomedOutFeature),
    period: cleanString(input.period),
    priorAnecdotes: cleanStringArray(input.priorAnecdotes),
    knownTranscriptSnippets: cleanStringArray(input.knownTranscriptSnippets),
    cardTitle: cleanString(input.cardTitle),
    cardBody: cleanString(input.cardBody),
    voiceName: cleanString(input.voiceName || DEFAULT_NARRATION_VOICE) || DEFAULT_NARRATION_VOICE,
    enableGoogleSearch: input.enableGoogleSearch === true,
  };
}

export function buildNarrationPrompt(input: NarrationPromptInput): string {
  const request = normalizeNarrationRequest(input);
  const contextBits = [
    request.topicSecondMostZoomedOutFeature,
    request.topicCountry,
  ].filter(Boolean);
  const contextInstruction = contextBits.length
    ? `Geographic context: ${contextBits.join(", ")}. Use this context to disambiguate places with similar names.`
    : "";
  const dontDoInstruction =
    "The listener already knows where they are, so avoid repeatedly restating the place name. NEVER ASK QUESTIONS BACK.";
  const alreadyKnowsInstruction = request.knownTranscriptSnippets.length
    ? `For continuity in this same topic, the user already knows about, ${request.knownTranscriptSnippets.join(" | ")}. Do not repeat these points verbatim; `
    : "";
  const promptLanguage = describeLanguageForPrompt(request.languageCode);
  const languageInstruction = promptLanguage ? `Speak in ${promptLanguage}.` : "";
  const answerInstruction = promptLanguage ? `Answer in ${promptLanguage}.` : "";

  switch (request.narrationMode) {
    case "anecdote": {
      const priorInstruction = request.priorAnecdotes.length
        ? `Already used anecdotes for this place (do NOT repeat these): ${request.priorAnecdotes.map((item, idx) => `${idx + 1}) ${item}`).join(" | ")}`
        : "";
      return `Tell one funny or surprising or interesting anecdote about ${request.topicTitle} that is said to actually have happend there.  ${contextInstruction} ${priorInstruction} ${alreadyKnowsInstruction} ${dontDoInstruction} ${languageInstruction}`;
    }
    case "historyOverview":
      return `Give a factual spoken history overview of ${request.topicTitle}. Stick to the facets dont tell subjective stuff. ${contextInstruction} ${alreadyKnowsInstruction} ${dontDoInstruction} ${languageInstruction}`;
    case "historicPeriod":
      return `Tell a factual spoken audio-guide segment about the ${request.period} period in ${request.topicTitle}. ${contextInstruction} ${alreadyKnowsInstruction} ${dontDoInstruction} ${languageInstruction}`;
    case "understandCard":
      return `Explain this concept card in a natural spoken style. Card title: ${request.cardTitle}. Card body: ${request.cardBody}. Stay faithful to the card content and keep it concise. ${contextInstruction} ${languageInstruction}`;
    default:
      return `Give a concise yet complete overview about ${request.topicTitle}. Stick to facts, do not tell subjective stuff. ${contextInstruction} ${answerInstruction} NEVER ask questions back.`;
  }
}

export function createGeminiLiveSetupPayload(input: LiveSetupInput = {}): GeminiLiveSetupPayload {
  const voiceName = cleanString(input.voiceName);
  const enableGoogleSearch = input.enableGoogleSearch === true;
  const systemInstructionText =
    cleanString(input.systemInstructionText) ||
    "You are a factual spoken audio guide for places. Reply in natural speech.";

  return {
    setup: {
      model: GEMINI_LIVE_BIDI_MODEL,
      systemInstruction: {
        parts: [{ text: systemInstructionText }],
      },
      generationConfig: {
        responseModalities: ["AUDIO"],
        thinkingConfig: {
          thinkingLevel: "minimal",
        },
        ...(voiceName
          ? {
              speechConfig: {
                voiceConfig: {
                  prebuiltVoiceConfig: {
                    voiceName,
                  },
                },
              },
            }
          : {}),
      },
      outputAudioTranscription: {},
      ...(enableGoogleSearch ? { tools: [{ googleSearch: {} }] } : {}),
    },
  };
}

export function createGeminiLiveRealtimeTextPayload(prompt: string): { realtimeInput: { text: string } } {
  return {
    realtimeInput: {
      text: cleanString(prompt),
    },
  };
}
